import { useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { RoleLite } from '@blauturquesa/shared/roles-lite';
import { Loading } from '../components/Loading';
import { useAuth } from '../features/auth/AuthProvider';
import { switchProfile } from '../features/auth/actions';

/**
 * Protegeix una pantalla segons el perfil ACTIU (§2.2). Si l'usuari té el perfil però no és l'actiu,
 * li ofereix canviar-hi en lloc de dir-li que no hi té accés (§10.1: avisar, no amagar).
 */
export function RequireProfile({ profiles, children }: { profiles?: readonly RoleLite[]; children: ReactNode }) {
  const state = useAuth();
  const location = useLocation();
  const { t } = useTranslation('shell');
  const [busy, setBusy] = useState(false);

  if (state.status === 'loading') return <Loading />;
  if (state.status === 'signedOut') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/entrar?next=${next}`} replace />;
  }
  if (!profiles) return children;
  if (state.activeRole !== null && profiles.includes(state.activeRole)) return children;
  if (!state.profileReady) return <Loading />;

  const switchable = profiles.find((p) => state.roles.includes(p));
  return (
    <div className="empty">
      <h2>{t('noAccess.title')}</h2>
      {switchable ? (
        <>
          <p className="muted">{t('noAccess.switchBody', { profile: t(`roles.${switchable}`) })}</p>
          <button
            type="button"
            className="btn-shiny"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              switchProfile(switchable, location.pathname + location.search).catch(() => setBusy(false));
            }}
          >
            {t('noAccess.switch', { profile: t(`roles.${switchable}`) })}
          </button>
        </>
      ) : (
        <p className="muted">{t('noAccess.body')}</p>
      )}
    </div>
  );
}
